import { client } from './api';
import type { FaxInboxView } from './faxRouting';

/**
 * The fax inbox as one list.
 *
 * A transmission that arrived and a document somebody assembled out of it are
 * one kind of thing to the person working the queue: something with a name and
 * some pages, to be tidied up, stapled together and turned into a referral.
 * They live in two tables for good reasons, and that is the system's business
 * rather than the reader's.
 *
 * ``kind`` is the only place the split is still visible, and it is here because
 * acting on a row needs it: renaming, merging and editing all reach a different
 * route depending on which table the row came from.
 */

export type FaxItemKind = 'arrival' | 'document';

export interface FaxItem {
  kind: FaxItemKind;
  id: string;
  /**
   * Null until somebody names it. Deliberately not filled in with a generated
   * title, because the screen needs to tell a name apart from a stand-in it
   * drew itself when it comes to offering a rename.
   */
  name: string | null;
  page_count: number | null;
  received_at: string;
  status: string;
  /** A document reports its sources' sender when they all agree. */
  fax_number_from: string | null;
  /**
   * The physician that number belongs to, when it is exactly one the caller
   * can see. Null when it names nobody or several, so the number stands alone.
   */
  sender_physician_name?: string | null;
  /** Only a document has one. An edit refuses a stale version. */
  version: number | null;
  /** An arrival every page of which already feeds a document. */
  filed: boolean;
  /**
   * The newest referral this document was copied into, when it has been, and
   * that referral's own processing status. Only a document can have started
   * one, and both stay null for a caller who may not read a referral.
   */
  intake_id?: string | null;
  intake_processing_status?: string | null;
  /**
   * The order this fax came back to answer, when a mark on it matched one.
   * A document has one when its sources answer exactly one order.
   */
  answers_order_id?: string | null;
  /**
   * Whether that order is still waiting on somebody to say the page is
   * signed. False once anybody has answered, so the question is asked once.
   */
  answers_order_awaiting_decision?: boolean;
  /**
   * That order's status, so a link can open the tab holding it. Answering the
   * question moves the order, so the tab is not fixed.
   */
  answers_order_status?: string | null;
  /** What kind of document it is, from Roger's list. */
  fax_type_id?: string | null;
  fax_type_name?: string | null;
  /** Whose it is. Saying so copies nothing; filing it to the chart does. */
  patient_id?: string | null;
  /** How a person recognises that patient. Null when the caller may not see them. */
  patient_label?: string | null;
  admission_id?: string | null;
  /** The draft referral it is being gathered for, when there is no patient. */
  intake_draft_id?: string | null;
  intake_draft_name?: string | null;
  /** The agency's own labels, named so the list does not need a second call. */
  tags?: { id: string; name: string }[];
  /**
   * When it was filed into a patient's chart, and the task it went on. Not
   * `filed` above, which means merged into a document.
   */
  chart_filed_at?: string | null;
  chart_task_id?: string | null;
  chart_task_label?: string | null;
  /** When it was taken out of the list without filing, such as a returned order. */
  archived_at?: string | null;
  /** True while the returned-order check on an arrival has not recorded a result. */
  order_match_pending?: boolean;
}

export interface FaxItemPage {
  items: FaxItem[];
  total: number;
}

/** The document a merge produced, as the server describes it. */
export interface MergedDocument {
  id: string;
  name: string;
  page_count: number;
  version: number;
}

/** One thing to merge, named the way the list names it. */
export interface MergeItemRef {
  kind: FaxItemKind;
  id: string;
}

export async function listFaxItems(
  limit = 25,
  offset = 0,
  includeFiled = false,
  status?: string,
  faxNumberFrom?: string,
  patientId?: string,
  faxTypeId?: string,
  view: FaxInboxView = 'working'
): Promise<FaxItemPage> {
  const response = await client.get<FaxItemPage, unknown, true>({
    query: {
      limit,
      offset,
      include_filed: includeFiled,
      status,
      fax_number_from: faxNumberFrom,
      patient_id: patientId,
      fax_type_id: faxTypeId,
      view,
    },
    responseType: 'json',
    throwOnError: true,
    url: '/fax-items',
  });
  return response.data;
}

export async function renameFaxItem(
  item: MergeItemRef,
  name: string
): Promise<FaxItem> {
  const response = await client.patch<FaxItem, unknown, true>({
    body: { name },
    responseType: 'json',
    throwOnError: true,
    url: `/fax-items/${item.kind}/${item.id}`,
  });
  return response.data;
}

/**
 * Staple several things in the list into one document.
 *
 * Whole items in the order given, rather than chosen pages: picking pages is
 * what the editor is for, and this is the bulk move somebody makes from the
 * list with several rows ticked. Merging a single item is allowed and is how
 * an arrival becomes something editable without its own pages being rewritten.
 */
export async function mergeFaxItems(
  name: string,
  items: MergeItemRef[]
): Promise<MergedDocument> {
  const response = await client.post<MergedDocument, unknown, true>({
    body: { name, items },
    responseType: 'json',
    throwOnError: true,
    url: '/fax-documents/merge',
  });
  return response.data;
}

/** A row of the list, named the way every fax-items route names it. */
export interface FaxItemRef {
  kind: FaxItemKind;
  id: string;
}

/** The list's filters, with the keys `/fax-items` takes. */
export interface FaxItemListQuery {
  include_filed: boolean;
  status?: string;
  fax_number_from?: string;
  patient_id?: string;
  fax_type_id?: string;
  view: FaxInboxView;
}

/**
 * Where a row sits in the filtered list. `previous` is the next newer row and
 * `next` the next older one. `position` is 1-based, and null when the row is
 * no longer in the list, such as one just filed.
 */
export interface FaxItemNeighbors {
  previous: FaxItemRef | null;
  next: FaxItemRef | null;
  position: number | null;
  total: number;
}

/** The intake a fax started, and the document it was built from. */
export interface FaxItemIntakeStarted {
  intake_id: string;
  document_count: number;
  fax_document_id: string;
}

export async function getFaxItem(
  kind: FaxItemKind,
  id: string
): Promise<FaxItem> {
  const response = await client.get<FaxItem, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/fax-items/${kind}/${id}`,
  });
  return response.data;
}

export async function getFaxItemNeighbors(
  kind: FaxItemKind,
  id: string,
  query: FaxItemListQuery
): Promise<FaxItemNeighbors> {
  const response = await client.get<FaxItemNeighbors, unknown, true>({
    query: { ...query },
    responseType: 'json',
    throwOnError: true,
    url: `/fax-items/${kind}/${id}/neighbors`,
  });
  return response.data;
}

/**
 * Start an intake from one fax plus any PDFs added to it.
 *
 * An unedited arrival is made into a document by the server first, and the
 * response names that document. The Content-Type is left to the browser so
 * the multipart boundary is set.
 */
export async function startIntakeFromFaxItem(
  kind: FaxItemKind,
  id: string,
  files: File[]
): Promise<FaxItemIntakeStarted> {
  const body = new FormData();
  files.forEach(file => body.append('files', file));
  const response = await client.post<FaxItemIntakeStarted, unknown, true>({
    body,
    headers: { 'Content-Type': null },
    responseType: 'json',
    throwOnError: true,
    url: `/fax-items/${kind}/${id}/intake`,
  });
  return response.data;
}

/** The intake several faxes started, and their documents in the order given. */
export interface FaxItemsIntakeStarted {
  intake_id: string;
  document_count: number;
  fax_document_ids: string[];
}

/**
 * Start one intake from several faxes, in the order given, then the added PDFs.
 *
 * All or nothing: the server refuses the whole request if any fax cannot be
 * used. The Content-Type is left to the browser so the boundary is set.
 */
export async function startIntakeFromFaxItems(
  items: FaxItemRef[],
  files: File[]
): Promise<FaxItemsIntakeStarted> {
  const body = new FormData();
  body.append(
    'items',
    JSON.stringify(items.map(item => ({ kind: item.kind, id: item.id })))
  );
  files.forEach(file => body.append('files', file));
  const response = await client.post<FaxItemsIntakeStarted, unknown, true>({
    body,
    headers: { 'Content-Type': null },
    responseType: 'json',
    throwOnError: true,
    url: '/fax-items/intake',
  });
  return response.data;
}
